/**
 * 🤝 EmployerBenefits — Iter 215 (2026-06-30)
 *
 * Replaces the bottom EcosystemTicker that the SA flagged as "a lot
 * of technical jargon floating at the base of the page." Now those
 * features are properly framed for partners in a 2x3 grid with
 * Lucide icons + plain-language descriptions.
 *
 * Each benefit card has:
 *   • A champagne-gold accented Lucide icon
 *   • A bold sans-serif title
 *   • A one-line partner-facing description
 *
 * Hover state: border slides to champagne gold, subtle lift.
 */
import {
  Receipt, ClipboardList, ShieldCheck, FileSpreadsheet, Award, BarChart3,
} from "lucide-react";

const BENEFITS = [
  {
    key:   "smartphone_receipt_capture",
    icon:  Receipt,
    title: "Smartphone Receipt Capture",
    body:  "Crews snap receipts; we OCR, classify, and route to your accounting in real time. Audit-trail attached, no spreadsheet sprawl.",
  },
  {
    key:   "unified_roster_management",
    icon:  ClipboardList,
    title: "Unified Roster Management",
    body:  "A single source of truth across hiring, scheduling, and active-worker compliance. No more weekly sync calls.",
  },
  {
    key:   "compliance_dashboard",
    icon:  ShieldCheck,
    title: "Real-Time Compliance Dashboard",
    body:  "Drug-test cadence, background-check renewals, certification expirations — surfaced before they become a liability.",
  },
  {
    key:   "direct_billable_hours",
    icon:  FileSpreadsheet,
    title: "Direct Billable Hours",
    body:  "Time stamps from the field flow into invoices weekly. Reconciled hours, no disputes, predictable cash.",
  },
  {
    key:   "workforce_pre_certification",
    icon:  Award,
    title: "Workforce Pre-Certification",
    body:  "Every candidate arrives with documented sobriety, training, and a TEA accountability sponsor. Start day one, not week three.",
  },
  {
    key:   "live_payroll_reporting",
    icon:  BarChart3,
    title: "Live Pay-Period Reporting",
    body:  "Drill down by site, supervisor, or trade. Export to your AP system in a click. Quarterly close in hours, not weeks.",
  },
];

export default function EmployerBenefits() {
  return (
    <section
      className="bg-[#F3F3EE] py-24 lg:py-28 border-t border-[#0A0612]/5"
      data-testid="employer-benefits"
    >
      <div className="px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
        {/* Section header */}
        <div className="mb-14 lg:mb-18 max-w-3xl">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#1B4332] font-bold mb-4">
            <span className="inline-flex items-center gap-2">
              <span className="h-1.5 w-6 bg-[#1B4332]" />
              For Our Employer Partners
            </span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0A0612] leading-tight">
            Six tools that turn TEA placements into{" "}
            <span className="text-[#1B4332]">your most reliable hires.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#0A0612]/70 leading-relaxed">
            Our Personal Advancement and Career Training (PACT) doesn&apos;t stop at
            placement — it follows your worker through every pay period, every
            compliance check, every promotion eligibility window.
          </p>
        </div>

        {/* 2x3 grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {BENEFITS.map((b) => {
            const Icon = b.icon;
            return (
              <article
                key={b.key}
                data-testid={`employer-benefit-${b.key}`}
                className="
                  bg-white rounded-2xl p-8
                  border border-[#0A0612]/5
                  hover:border-[#D4AF37]/50
                  hover:-translate-y-1
                  transition-all duration-300 ease-out
                  shadow-sm hover:shadow-md
                "
              >
                <div className="h-10 w-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-5">
                  <Icon className="h-5 w-5 text-[#D4AF37]" strokeWidth={2.2} />
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight text-[#0A0612] mb-3">
                  {b.title}
                </h3>
                <p className="text-sm text-[#0A0612]/70 leading-relaxed">
                  {b.body}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
